<script setup lang="ts">
  import BaseModal from '@/shared/components/BaseModal/BaseModal.vue'
  import CloseIcon from '@/shared/components/icons/CloseIcon.vue'
  import type {
    Emits,
    Props,
  } from '@/entities/Flow/components/CyberSecurityModal/CyberSecurityModal.types'
  import { watch } from 'vue'
  import { useGoogleAnalytics } from '@/shared/composables'
  import { useRoute } from 'vue-router'
  import type { InboundFlowRoutes, OutboundFlowRoutes } from '@/shared/types/router.types'

  const props = withDefaults(defineProps<Props>(), {
    isAddButtonShown: false,
  })

  const emit = defineEmits<Emits>()

  const modelValue = defineModel<boolean>({ default: false })

  const gaService = useGoogleAnalytics()
  const route = useRoute()

  const closeModal = () => {
    modelValue.value = false
  }

  const onAddBtnClicked = () => {
    emit('add-cyber-cover')

    if (!props.quoteId) throw new Error('The quote number is undefined.')
    gaService.sendAddCyberToCoverage(
      route.name as InboundFlowRoutes | OutboundFlowRoutes,
      props.quoteId
    )
  }

  watch(modelValue, (newValue) => {
    if (newValue) {
      gaService.sendCyberPopupOpened(
        route.name as InboundFlowRoutes | OutboundFlowRoutes,
        props.quoteId
      )
    }
  })
</script>

<template>
  <BaseModal
    id="cyber-security-modal"
    :is-open="modelValue"
    :show-close-button="false"
    @close="closeModal"
  >
    <div class="cyber-modal-content">
      <div class="cyber-modal-content__header">
        <div class="cyber-modal-content__close-icon-wrapper">
          <CloseIcon
            color="#394B5ACC"
            class="cyber-modal-content__close-icon"
            @click="closeModal"
          />
        </div>
        <h2 class="cyber-modal-content__title">{{ $t('cyber_security_addon.name') }}</h2>
      </div>
      <div class="cyber-modal-content__body">
        <picture class="cyber-modal-content__image-wrapper">
          <source srcset="@/shared/assets/images/lock.webp" type="image/webp" />
          <img src="@/shared/assets/images/lock.png" alt="Secure lock icon" />
        </picture>
        <h3 class="cyber-modal-content__subtitle">
          {{ $t('cyber_security_addon.popup.header.1') }}
        </h3>
        <div class="cyber-modal-content__covered">
          <div v-for="i in 6" :key="i" class="cyber-modal-content__item">
            <div class="cyber-modal-content__item-title">
              {{ $t(`cyber_security_addon.popup.block.${i}.header`) }}
            </div>
            <div class="cyber-modal-content__item-description">
              {{ $t(`cyber_security_addon.popup.block.${i}.desc`) }}
            </div>
          </div>
        </div>
        <h3 class="cyber-modal-content__subtitle">
          {{ $t('cyber_security_addon.popup.header.2') }}
        </h3>
        <ul class="cyber-modal-content__not-covered">
          <li v-for="i in 4" :key="i" class="cyber-modal-content__not-covered-item">
            {{ $t(`cyber_security_addon.popup.list.${i}`) }}
          </li>
        </ul>
      </div>
      <div class="cyber-modal-content__buttons">
        <button
          v-if="isAddButtonShown && !cyberState"
          class="cyber-modal-content__button cyber-modal-content__button--add"
          @click.stop="onAddBtnClicked"
        >
          {{ $t('cyber_security_addon.popup.button.add') }}
        </button>
        <button
          class="cyber-modal-content__button cyber-modal-content__button--close"
          @click.stop="closeModal"
        >
          {{ $t('qic.close') }}
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<style scoped lang="scss">
  @import '@/shared/assets/styles/utils';

  :deep(.modal__content) {
    @include mobile-flow {
      padding: 16px 16px 40px;
      max-height: unset;
    }
  }

  .cyber-modal-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    max-width: 464px;
    max-height: 90vh;

    &__header {
      position: sticky;
      top: 0;
      width: 100%;
      padding-bottom: 20px;
    }

    &__close-icon-wrapper {
      display: flex;
      justify-content: flex-end;
      cursor: pointer;
    }

    &__title {
      display: flex;
      text-align: center;
      flex-direction: column;
      align-items: center;
      align-self: stretch;
      color: var(--colorTxtSrfPrimary);

      @include title-5-plus;

      @include mobile-flow {
        @include title-6-plus;
      }
    }

    &__body {
      display: flex;
      padding-top: 20px;
      flex-direction: column;
      align-items: center;
      gap: 32px;
      align-self: stretch;
      overflow-y: auto;
      flex-grow: 1;
      scroll-behavior: smooth;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: #c5c5c5;
        border-radius: 4px;

        &:hover {
          background-color: #a0a0a0;
        }
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }
    }

    &__image-wrapper {
      display: flex;
      height: 220px;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      align-self: stretch;
      border-radius: 32px;
      background: var(--colorBgSrfTertiary);
    }

    &__subtitle {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 8px;
      align-self: stretch;

      @include caption-2-plus;
    }

    &__covered {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 24px;
      align-self: stretch;
    }

    &__item {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
      align-self: stretch;

      @include mobile-flow {
        gap: 4px;
      }
    }

    &__item-title {
      color: #1c1c1c;

      @include caption-4-plus;
    }

    &__item-description {
      color: rgb(57 75 90 / 80%);

      @include caption-5;
    }

    &__not-covered {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      align-self: stretch;
      gap: 24px;
    }

    &__not-covered-item {
      color: #1c1c1c;

      @include caption-4-plus;
    }

    &__buttons {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 24px;
      align-self: stretch;
      margin-top: 32px;

      @include mobile-flow {
        gap: 16px;
      }
    }

    &__button {
      display: flex;
      padding: 14px 24px;
      justify-content: center;
      align-items: center;
      gap: 12px;
      align-self: stretch;
      border-radius: 12px;
      cursor: pointer;

      @include caption-4-plus;

      @include mobile-flow {
        @include caption-4-plus-plus;
      }

      &--add {
        color: #f1f4f6;
        background: var(--colorBgBtnPrimaryDefault);
      }

      &--close {
        color: var(--colorTxtSrfAction);
        border: 1px solid var(--colorBgSrfAccentBrand);
        background: var(--colorBgSrfPrimary);
      }
    }
  }
</style>
